import React, { KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon } from '@/core/constants/webComponents';
import './EditCompetitorsModal.scss';

interface NamedKeywordsGroup {
  name: string;
  keywords: string[];
}

interface EditableCompetitorGroup {
  id: string;
  name: string;
  keywords: string[];
  keywordInput: string;
  expanded: boolean;
}

interface EditCompetitorsModalProps {
  groups: NamedKeywordsGroup[];
  onChange: (nextGroups: NamedKeywordsGroup[]) => void;
  onFinish?: () => void;
  onClose: () => void;
  translationPrefix?: 'newSetup.competitorsModal' | 'newSetup.groupsModal' | 'newSetup.themesModal' | 'newSetup.tagsModal';
  allowGroupManagement?: boolean;
  showGroupNameField?: boolean;
}

const toEditableGroups = (groups: NamedKeywordsGroup[]): EditableCompetitorGroup[] =>
  groups.map((group, index) => ({
    id: `${group.name || 'group'}-${index}`,
    name: group.name,
    keywords: [...group.keywords],
    keywordInput: '',
    expanded: true,
  }));

export const EditCompetitorsModal: React.FC<EditCompetitorsModalProps> = ({
  groups,
  onChange,
  onFinish,
  onClose,
  translationPrefix = 'newSetup.competitorsModal',
  allowGroupManagement = true,
  showGroupNameField = true,
}) => {
  const { t } = useTranslation();
  const [editableGroups, setEditableGroups] = useState<EditableCompetitorGroup[]>(() => toEditableGroups(groups));
  const translationFallbackPrefix = 'newSetup.competitorsModal';
  const modalId = translationPrefix.replaceAll('.', '-');
  const modalT = (key: string, options?: Record<string, unknown>) =>
    t([`${translationPrefix}.${key}`, `${translationFallbackPrefix}.${key}`], options);

  const syncGroups = (nextGroups: EditableCompetitorGroup[]) => {
    setEditableGroups(nextGroups);

    const payload = nextGroups.map(({ name, keywords }) => ({
      name: name.trim(),
      keywords,
    }));

    onChange(payload);
  };

  const updateGroupById = (groupId: string, updater: (group: EditableCompetitorGroup) => EditableCompetitorGroup) => {
    const nextGroups = editableGroups.map((group) => (group.id === groupId ? updater(group) : group));
    syncGroups(nextGroups);
  };

  const toggleGroup = (groupId: string) => {
    updateGroupById(groupId, (group) => ({ ...group, expanded: !group.expanded }));
  };

  const removeGroup = (groupId: string) => {
    if (!allowGroupManagement) {
      return;
    }

    const nextGroups = editableGroups.filter((group) => group.id !== groupId);
    syncGroups(nextGroups);
  };

  const updateGroupName = (groupId: string, nextName: string) => {
    updateGroupById(groupId, (group) => ({ ...group, name: nextName }));
  };

  const updateGroupKeywordInput = (groupId: string, keywordInput: string) => {
    setEditableGroups((currentGroups) =>
      currentGroups.map((group) => (group.id === groupId ? { ...group, keywordInput } : group)),
    );
  };

  const addKeywordToGroup = (groupId: string) => {
    const nextGroups = editableGroups.map((group) => {
      if (group.id !== groupId) {
        return group;
      }

      const normalizedKeyword = group.keywordInput.trim();
      if (!normalizedKeyword) {
        return group;
      }

      const hasKeywordAlready = group.keywords.some((keyword) => keyword.toLowerCase() === normalizedKeyword.toLowerCase());
      if (hasKeywordAlready) {
        return { ...group, keywordInput: '' };
      }

      return {
        ...group,
        keywords: [...group.keywords, normalizedKeyword],
        keywordInput: '',
      };
    });

    syncGroups(nextGroups);
  };

  const removeKeywordFromGroup = (groupId: string, keywordToRemove: string) => {
    const nextGroups = editableGroups.map((group) => {
      if (group.id !== groupId) {
        return group;
      }

      return {
        ...group,
        keywords: group.keywords.filter((keyword) => keyword !== keywordToRemove),
      };
    });

    syncGroups(nextGroups);
  };

  const handleKeywordInputKeyDown = (event: KeyboardEvent<HTMLInputElement>, groupId: string) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addKeywordToGroup(groupId);
      return;
    }

    const currentGroup = editableGroups.find((group) => group.id === groupId);
    if (!currentGroup) {
      return;
    }

    if (event.key === 'Backspace' && !currentGroup.keywordInput && currentGroup.keywords.length > 0) {
      const keywordToRemove = currentGroup.keywords.at(-1);
      if (!keywordToRemove) {
        return;
      }

      removeKeywordFromGroup(groupId, keywordToRemove);
    }
  };

  const addGroup = () => {
    if (!allowGroupManagement) {
      return;
    }

    const defaultName = `${modalT('newGroupDefaultName')} ${editableGroups.length + 1}`;

    const nextGroup: EditableCompetitorGroup = {
      id: `${Date.now()}-${editableGroups.length}`,
      name: defaultName,
      keywords: [],
      keywordInput: '',
      expanded: true,
    };

    syncGroups([...editableGroups, nextGroup]);
  };

  const handleClose = () => {
    onClose();
  };

  const handleFinish = () => {
    onFinish?.();
    onClose();
  };

  return (
    <div className="new-setup__modal-overlay">
      <dialog open className="new-setup__competitors-modal" aria-labelledby={`${modalId}-title`}>
        <header className="new-setup__competitors-modal-header">
          <button
            type="button"
            className="new-setup__competitors-modal-close"
            onClick={handleClose}
            aria-label={modalT('close')}
          >
            <BdsIcon theme="outline" name="close" size="medium" aria-hidden="true" />
          </button>

          <h2 id={`${modalId}-title`} className="new-setup__competitors-modal-title">
            {modalT('title')}
          </h2>
          <p className="new-setup__competitors-modal-subtitle">{modalT('subtitle')}</p>
        </header>

        <div className="new-setup__competitors-modal-content">
          <div className="new-setup__competitors-modal-divider" />

          <div className="new-setup__competitors-modal-copy">
            <h3 className="new-setup__competitors-modal-copy-title">{modalT('groupsTitle')}</h3>
            <p className="new-setup__competitors-modal-copy-text">{modalT('groupsDescription')}</p>
          </div>

          <div className="new-setup__competitors-group-list">
            {editableGroups.map((group, index) => (
              <article key={group.id} className="new-setup__competitors-group-card">
                <header className="new-setup__competitors-group-header">
                  <div className="new-setup__competitors-group-title-wrap">
                    <h4 className="new-setup__competitors-group-title">{group.name || modalT('unnamedGroup')}</h4>
                    <span className="new-setup__competitors-group-count">{group.keywords.length} kw</span>
                  </div>

                  <div className="new-setup__competitors-group-actions">
                    {allowGroupManagement && (
                      <button
                        type="button"
                        className="new-setup__competitors-group-delete"
                        onClick={() => removeGroup(group.id)}
                        aria-label={modalT('removeGroupAria', { group: group.name || index + 1 })}
                      >
                        <BdsIcon
                          theme="outline"
                          name="trash"
                          size="medium"
                          color="var(--color-negative, #A80B0B)"
                          aria-hidden="true"
                        />
                      </button>
                    )}
                    <button
                      type="button"
                      className="new-setup__competitors-group-toggle"
                      onClick={() => toggleGroup(group.id)}
                      aria-label={modalT('toggleGroupAria', { group: group.name || index + 1 })}
                    >
                      <BdsIcon theme="outline" name={group.expanded ? 'arrow-up' : 'arrow-down'} size="small" aria-hidden="true" />
                    </button>
                  </div>
                </header>

                {group.expanded && (
                  <div className="new-setup__competitors-group-body">
                    {showGroupNameField && (
                      <div className="new-setup__competitors-group-name-field">
                        <label className="new-setup__competitors-field-label" htmlFor={`competitor-group-name-${group.id}`}>
                          {modalT('groupNameLabel')}
                        </label>
                        <input
                          id={`competitor-group-name-${group.id}`}
                          className="new-setup__competitors-field-input"
                          value={group.name}
                          placeholder={modalT('groupNamePlaceholder')}
                          onChange={(event) => updateGroupName(group.id, event.target.value)}
                        />
                      </div>
                    )}

                    <div className="new-setup__competitors-keywords-field">
                      <p className="new-setup__competitors-keywords-count">
                        {modalT('keywordsCountLabel', { count: group.keywords.length })}
                      </p>

                      <div className="new-setup__competitors-input-wrap">
                        <div className="new-setup__competitors-chip-grid">
                          {group.keywords.map((keyword) => (
                            <span key={`${group.id}-${keyword}`} className="new-setup__competitors-chip">
                              <span className="new-setup__competitors-chip-label">{keyword}</span>
                              <button
                                type="button"
                                className="new-setup__competitors-chip-remove"
                                onClick={() => removeKeywordFromGroup(group.id, keyword)}
                                aria-label={modalT('removeKeywordAria', { keyword })}
                              >
                                <span className="new-setup__competitors-chip-remove-icon" aria-hidden="true" />
                              </button>
                            </span>
                          ))}

                          <input
                            className="new-setup__competitors-input"
                            value={group.keywordInput}
                            placeholder={modalT('addKeywordPlaceholder')}
                            onChange={(event) => updateGroupKeywordInput(group.id, event.target.value)}
                            onKeyDown={(event) => handleKeywordInputKeyDown(event, group.id)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>

          {allowGroupManagement && (
            <button type="button" className="new-setup__competitors-add-group" onClick={addGroup}>
              <span className="new-setup__competitors-add-group-icon" aria-hidden="true">
                <BdsIcon theme="outline" name="add" size="medium" color="var(--color-primary, #1E6BF1)" aria-hidden="true" />
              </span>
              <span>{modalT('addGroup')}</span>
            </button>
          )}
        </div>

        <footer className="new-setup__competitors-modal-footer">
          <button type="button" className="new-setup__competitors-modal-confirm" onClick={handleFinish}>
            {modalT('finish')}
          </button>
        </footer>
      </dialog>
    </div>
  );
};
